import type { TrustIDOAuthConfig } from "./types";
import { generateCodeChallenge, generateCodeVerifier } from "./pkce";

const STORAGE_KEY_STATE = "trustid_oauth_state";
const STORAGE_KEY_VERIFIER = "trustid_oauth_code_verifier";

export function generateState(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function buildAuthorizationUrl(
  config: TrustIDOAuthConfig
): Promise<string> {
  const state = generateState();
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  storeAuthParams(state, codeVerifier);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.client_id,
    redirect_uri: config.redirect_uri,
    scope: config.scope || "openid email phone",
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });

  if (config.idp_hint) {
    params.set("kc_idp_hint", config.idp_hint);
  }

  if (config.nonce) {
    params.set("nonce", config.nonce);
  }

  if (config.extra_params) {
    for (const [key, value] of Object.entries(config.extra_params)) {
      params.set(key, value);
    }
  }

  return `${config.authorization_endpoint}?${params.toString()}`;
}

export function storeAuthParams(state: string, codeVerifier: string): void {
  sessionStorage.setItem(STORAGE_KEY_STATE, state);
  sessionStorage.setItem(STORAGE_KEY_VERIFIER, codeVerifier);
}

export function retrieveAndClearAuthParams(): {
  state: string | null;
  codeVerifier: string | null;
} {
  const state = sessionStorage.getItem(STORAGE_KEY_STATE);
  const codeVerifier = sessionStorage.getItem(STORAGE_KEY_VERIFIER);
  sessionStorage.removeItem(STORAGE_KEY_STATE);
  sessionStorage.removeItem(STORAGE_KEY_VERIFIER);
  return { state, codeVerifier };
}
